import { useEffect, useState } from "react";
import { isEmpty } from "lodash-es";

import { SummitList } from "./SummitList";
import { MachinesPanel } from "./MachinesPanel";
import { LogsPanel } from "./LogsPanel";
import { isLatestLogError } from "./statusResolvers";
import { WorkspaceRow, Panel } from "./summitStyled";

import { useEodDevices } from "../../hooks/summit/useEodDevices";
import { useGetReportingStatisticsDevices } from "../../hooks/summit/useReportingStatisticsDevices";
import { useEodLogs } from "../../hooks/summit/useEodLogs";

// WHAT: the three-panel Summit workspace (find user → pick machine → view
// logs). This component owns all the data-fetching and selection state;
// the actual per-panel markup lives in SummitList, MachinesPanel, and
// LogsPanel, so this file's job is orchestration only — reading it top to
// bottom should tell you *what* the workspace does, not *how* each panel
// renders.
export const SummitWorkspace: React.FC = () => {
  // ── Step 1: user + add-on selection (delegated to SummitList) ──────────
  const [selectedUser, setSelectedUser] = useState<any | null>(null);
  const [selectedAddOn, setSelectedAddOn] = useState("");
  const managerId: string | undefined = selectedUser?.parent_user_id || undefined;
  // A user is a "sub-user" whenever the list API gave us a parent_user_id
  // for them — that's the one signal the API exposes for this, so we
  // derive it here rather than trusting any other field.
  const isSubUser = !!selectedUser?.parent_user_id;

  // ── Step 2: machines ─────────────────────────────────────────────────
  const getOwnEodDevices = useEodDevices(selectedUser?.id);
  const getOwnReportingDevices = useGetReportingStatisticsDevices(selectedUser?.id);

  const [machines, setMachines] = useState<any[]>([]);
  const [machinesLoading, setMachinesLoading] = useState(false);
  const [machineFilterText, setMachineFilterText] = useState("");
  const [selectedMachine, setSelectedMachine] = useState<any>(null);
  // "Show all machines" toggle — only meaningful (and only shown) for
  // sub-users, since it controls whether we ask the API for the parent
  // account's full machine list instead of just this user's own.
  // Defaults to false: first load for any user, sub-user or not, should
  // only show their own machines unless the rep explicitly asks for more.
  const [showAllMachines, setShowAllMachines] = useState(false);

  // ── Step 3: logs ─────────────────────────────────────────────────────
  // Per confirmation: logs are always fetched via useEodLogs by deviceId,
  // regardless of which add-on the machine belongs to.
  const getEodLogs = useEodLogs();
  const [logs, setLogs] = useState<any[]>([]);
  const [logsLoading, setLogsLoading] = useState(false);
  const [statusFilter, setStatusFilter] = useState<string | undefined>(undefined);

  // Reset the toggle whenever the selected user changes, so it doesn't
  // silently carry over "show all machines = true" from a previously
  // selected sub-user onto the next one.
  useEffect(() => {
    setShowAllMachines(false);
  }, [selectedUser?.id]);

  // WHAT: fetch machines for the current user/add-on/toggle combination,
  // and reset everything downstream (selected machine, its logs) first.
  // WHY reset logs here too: previously only the machine list and the
  // selected-machine pointer were cleared on user/add-on change — the old
  // `logs` array was left sitting in state. Because Step 3 only renders
  // when `selectedMachine` is set, that stale data wasn't visible right
  // away, but it would flash back on screen the moment a machine was
  // re-selected, until the new fetch resolved. Clearing it eagerly here
  // means switching users/add-ons empties Machines *and* Logs immediately,
  // with no stale-data flash in between.
  useEffect(() => {
    setSelectedMachine(null);
    setMachineFilterText("");
    setLogs([]);

    if (!selectedUser || isEmpty(selectedAddOn)) {
      setMachines([]);
      return;
    }

    let cancelled = false;
    setMachinesLoading(true);

    (async () => {
      try {
        const ownFn = selectedAddOn === "eod_connect" ? getOwnEodDevices : getOwnReportingDevices;

        // showAllMachines maps directly to the API's all_user_machines
        // flag — false (default) returns just this user's own machines,
        // true returns the full set for the account (only relevant/exposed
        // for sub-users, see the checkbox in MachinesPanel).
        const ownResponse = await ownFn(showAllMachines);
        const ownList = (ownResponse?.list ?? []).map((m: any) => ({ ...m, _owner: "own" }));

        if (!cancelled) setMachines(ownList);
      } catch (err) {
        // TODO: surface this to the rep via a toast/message component —
        // right now a failed fetch just quietly shows an empty machine list.
        console.error("Failed to fetch machines", err);
        if (!cancelled) setMachines([]);
      } finally {
        if (!cancelled) setMachinesLoading(false);
      }
    })();

    return () => {
      cancelled = true;
    };
    // Only re-run when the user/add-on/toggle actually changes — the fetch
    // functions close over selectedUser?.id/managerId each render, so
    // including them here would cause redundant re-fetches.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedUser?.id, selectedAddOn, managerId, showAllMachines]);

  const visibleMachines = isEmpty(machineFilterText)
    ? machines
    : machines.filter(
      (m) =>
        m.machine_name?.toLowerCase().includes(machineFilterText.toLowerCase()) ||
        m.device_id?.toLowerCase().includes(machineFilterText.toLowerCase())
    );

  const loadLogsForMachine = async (deviceId: string) => {
    setLogsLoading(true);
    setStatusFilter(undefined);
    try {
      const response = await getEodLogs(deviceId);
      const logsList = response?.list ?? [];
      setLogs(logsList);

      // Reflect the latest log's outcome back onto this machine's status
      // dot in the sidebar: if the newest log entry (top row of the table)
      // is an error, force that machine's dot red by flipping
      // last_scan_status to false. We only ever move it to false here —
      // we never flip it back to true off log data, since the machines
      // list API's own last_scan_status is the source of truth for the
      // "no error" case.
      if (logsList.length > 0) {
        const hasLatestError = isLatestLogError(logsList);
        setMachines((prev) =>
          prev.map((m) =>
            m.device_id === deviceId ? { ...m, last_scan_status: !hasLatestError } : m
          )
        );
      }
    } catch (err) {
      console.error("Failed to fetch logs", err);
      setLogs([]);
    } finally {
      setLogsLoading(false);
    }
  };

  const handleMachineSelect = (machine: any, deviceId: string) => {
    setSelectedMachine(machine);
    setStatusFilter(undefined);
    loadLogsForMachine(deviceId);
  };

  const filteredLogs = logs.filter((log) => {
    if (statusFilter && log?.status?.toString().toLowerCase() !== statusFilter) return false;
    return true;
  });

  return (
    <WorkspaceRow>
      {/* STEP 1 — Find user */}
      <Panel width={260}>

        <SummitList
          onSelectionChange={(user, addOn) => {
            setSelectedUser(user);
            setSelectedAddOn(addOn);
          }}
        />


      </Panel>

      {/* STEP 2 — Machines */}
      <Panel width={260}>

        <MachinesPanel
          selectedUser={selectedUser}
          selectedAddOn={selectedAddOn}
          isSubUser={isSubUser}
          showAllMachines={showAllMachines}
          onShowAllMachinesChange={setShowAllMachines}
          machineFilterText={machineFilterText}
          onMachineFilterTextChange={setMachineFilterText}
          machinesLoading={machinesLoading}
          visibleMachines={visibleMachines}
          selectedMachine={selectedMachine}
          onMachineSelect={handleMachineSelect}
        />

      </Panel>

      {/* STEP 3 — Logs */}
      <Panel>
        <LogsPanel
          selectedUser={selectedUser}
          selectedMachine={selectedMachine}
          filteredLogs={filteredLogs}
          logsLoading={logsLoading}
          statusFilter={statusFilter}
          onStatusFilterChange={setStatusFilter}
          onRefresh={() => selectedMachine && loadLogsForMachine(selectedMachine.device_id)}
        />

      </Panel>
    </WorkspaceRow>
  );
};
